import {
  CONTACT_PAGE_ANCHOR_JUMP_POINTS,
  DEVIATION_PAGE_LANDING_ACTION,
  DEVIATION_PATHS,
  PROJECTS,
  SHOP_PATHS,
  URLS,
  USER_PROFILE_BACKROOM_SECTIONS,
  USER_PROFILE_POSTS_SUB_PATHS,
  USER_PROFILE_SECTION_PATHS,
  USER_PROFILE_SHOP_SUB_PATHS,
} from './constants';
import { Request } from 'express';
import ddt from '@wix/da-ddt';
import trim from 'lodash/trim';
import omitBy from 'lodash/omitBy';
import { PapiDeviation } from '@wix/da-papi-types';

export type Protocol = 'http' | 'https';
export const ALL_FOLDER_ID = -1;
export const SCRAPS_FOLDER_ID = -2;
export const ALL_FOLDER_NAME = 'All';
export const SCRAPS_FOLDER_NAME = 'Scraps';

export interface UrlConfig {
  protocol: Protocol;
  domain: string;
  port?: number;
  mountPointPath: string;
  environmentFlags: string[];
  isProduction: boolean;
}

export interface UserGalleryParams {
  username: string;
  gallectionType: string;
  folderId?: number;
  folderName?: string;
  sortOrder?: string;
  searchQuery?: string;
  anchor?: boolean;
  deviationIdSeed?: number;
  order?: 'newest' | 'relevance';
}

export interface UserSubscriptionParams {
  username: string;
  tierDeviationId?: number;
  tierContentType?: 'deviations' | 'posts';
  anchor?: boolean;
  landedFromCheckout?: boolean;
}

export interface UserPostsParams {
  username: string;
  subPath: USER_PROFILE_POSTS_SUB_PATHS;
  sortOrder?: string;
  anchor?: boolean;
}

export interface CoreMembershipLinkParams {
  referer?: string;
  subproductId?: number;
  forUserId?: number;
  hasCoreMembership?: boolean;
}

export interface MessagesNotesParams {
  folderId?: number;
  noteId?: number;
  page?: number;
  q?: string;
}

export interface TagParams {
  order?: 'most-recent' | 'this-week';
}

export interface ProfileQueryParams {
  flow?: 'founder-leave' | 'invite' | 'close';
}

function getPathParts(rawPathParts: any[]) {
  return rawPathParts.filter(Boolean).join('/');
}

function toQueryStringParams(obj: object) {
  const params = new URLSearchParams();
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key) && obj[key] !== undefined) {
      params.set(key, obj[key]);
    }
  }
  return params.toString();
}

function getQueryString(obj: object) {
  const params = toQueryStringParams(obj);
  return params.length > 0 ? `?${params}` : '';
}

/**
 * This lib is very simple, its basically an ENUM of known public
 * canonical URLs. With some helpers to generate paths for those
 * known URLs.
 *
 * Additonally it provides a way to override local development env
 * with local urls, so that when developing locally you can link to
 * your localhost rather than production base url (www.da.com)
 *
 * If you are writing a helper method remember to use toEnvironment()
 * this will handle to .com/.lan rewriting but also handle local
 * dev where you are on node.deviantart.lan:3000 here the project
 * param allows us to detect you are running the project locally
 * and redirect link to node. instead of .lan
 *
 * buildFromPath can be used for adhoc links and only considers
 * .com vs .lan so no local dev handling.
 *
 * For global urls URLS.N use the to() helper to make them dev friendly.
 *
 * But remember that if your url takes common params, make a helper
 * that uses toEnvironment as its the most dev friendly way.
 */
export default class Url {
  isProduction: boolean = true;
  environmentFlags: string[] = [];

  protocol: Protocol;
  domain: string;
  port?: number;
  mountPointPath: string;
  initSSRDone: boolean = false;

  constructor() {
    this.initClientSide();
  }

  setEnv(isProduction: boolean, environmentFlags: string[] = []) {
    this.isProduction = isProduction;
    if (environmentFlags.length) {
      this.environmentFlags = environmentFlags;
    }
  }

  setBase(protocol: Protocol, domain: string, mountPointPath: string, port?: number) {
    this.protocol = protocol;
    this.domain = domain;
    this.mountPointPath = mountPointPath;
    this.port = port;
  }

  initServerSide(req: Request) {
    this.initSSRDone = true;
    try {
      const expressProtocol = process.env.NODE_ENV === 'production' ? 'https' : req.protocol;
      const parsed = new URL(`${expressProtocol}://${req.get('host')}${req.originalUrl}`);
      const path = `${parsed.pathname}${parsed.search}`;
      this.setBase(
        parsed.protocol === 'http:' ? 'http' : 'https',
        parsed.hostname,
        path.replace(req.url, ''),
        Number(parsed.port)
      );
    } catch (e) {}
  }

  initClientSide() {
    if (typeof document === 'undefined' || typeof window === 'undefined') {
      return;
    }
    const config: UrlConfig = window['__URL_CONFIG__'];
    if (!config) {
      ddt.error('URL', 'No clientside URL config found!');
      return;
    }
    this.setBase(config.protocol, config.domain, config.mountPointPath, config.port);
    this.environmentFlags = config.environmentFlags;
    this.isProduction = config.isProduction;
  }

  toConfig(): UrlConfig {
    const { protocol, domain, mountPointPath, port, environmentFlags, isProduction } = this;
    return {
      protocol,
      domain,
      port,
      mountPointPath,
      environmentFlags,
      isProduction,
    };
  }

  baseUrl(trailing = true): string {
    const port = this.port ? `:${this.port}` : '';
    const url = `${this.protocol}://${this.domain}${port}${this.mountPointPath}`;

    if (trailing) {
      return `${url.replace(/\/$/, '')}/`;
    }

    return url.replace(/\/$/, '');
  }

  origin(): string {
    const port = this.port ? `:${this.port}` : '';
    return `${this.protocol}://${this.domain}${port}`;
  }

  /**
   * Builds a url that works on both prod and VM
   *
   * @param path path or full URL
   * @param baseUrl default is ROOT
   * @returns string
   */
  buildFromPath(path: string = '', baseUrl: string = ''): string {
    let url = baseUrl ? baseUrl : URLS.ROOT;
    if (path && path.includes('://')) {
      // path is a full URL, use that instead
      url = path;
      path = '';
    }
    if (!this.isProduction) {
      url = url.replace('deviantart.com', 'deviantart.lan');
      url = url.replace('sta.sh', 'sta.nk');
    }
    if (path) {
      if (url.endsWith('/') || path.startsWith('/')) {
        url += path;
      } else {
        url += `/${path}`;
      }
    }
    // If we set the domain to .lan, replace all .coms
    if (this.domain === 'www.deviantart.lan') {
      url = url.replace('deviantart.com', 'deviantart.lan');
    }
    return url;
  }

  /**
   * Use this instead of URLS.N directly so VM links work nicely.
   *
   * Remember if your url takes options, create a helper instead.
   *
   * @param globalUrl
   */
  to(globalUrl: string): string {
    return this.buildFromPath(globalUrl);
  }

  /** Like buildFromPath but replaces origin even if given a full url with a protocol */
  replaceWithCurrentOrigin(url: string): string {
    // TODO: there's some duplication here with the above
    // maybe try to make the protocol check in buildFromPath optional?
    const parsed = new URL(url);
    return `${this.origin()}${parsed.pathname}${parsed.search}`;
  }

  userLink(username: string, path: string = '', anchor: boolean = false): string {
    if (!username) {
      return '';
    }
    username = username.toLowerCase();
    if (path !== '') {
      path = `/${path.replace(/^\//, '')}`;
    }

    const fullUrl = this.toEnvironmentUrl(`/${username}${path}`, PROJECTS.PROFILE);

    return anchor ? `${fullUrl}#content` : fullUrl;
  }

  userGalleryLink(params: UserGalleryParams): string {
    return this.toEnvironmentUrl(this.userGalleryPath(params), PROJECTS.PROFILE);
  }

  userGalleryPath({
    username,
    gallectionType,
    folderId,
    folderName,
    sortOrder,
    searchQuery,
    anchor,
    deviationIdSeed,
    order,
  }: UserGalleryParams): string {
    const pathParts = [
      (username ?? '').toLowerCase(),
      {
        gallery: USER_PROFILE_SECTION_PATHS.GALLERY,
        collection: USER_PROFILE_SECTION_PATHS.FAVOURITES,
        private_collection: USER_PROFILE_SECTION_PATHS.PRIVATE_COLLECTIONS,
      }[gallectionType],
    ];
    if (!searchQuery && folderId && folderName) {
      if (![ALL_FOLDER_ID, SCRAPS_FOLDER_ID].includes(folderId)) {
        // ^ these folders are identified by name
        pathParts.push(`${folderId}`);
      }
      pathParts.push(folderName.toLowerCase().replace(/[^A-Za-z0-9]+/g, '-'));
    }

    const query = getQueryString({
      q: searchQuery ? searchQuery : undefined,
      sort: folderId === ALL_FOLDER_ID || searchQuery ? sortOrder : undefined,
      deviationid: deviationIdSeed,
      order: folderId === ALL_FOLDER_ID && gallectionType === 'gallery' ? order : undefined,
    });

    return `/${pathParts.join('/')}${query}${anchor ? '#content' : ''}`;
  }

  premiumGalleryPurchasePath(folderId: number): string {
    return `/premium_folders/modal/purchase/${folderId}`;
  }

  contactLink(): string {
    return this.toEnvironmentUrl('/contact-us', PROJECTS.PONIES);
  }

  directedContactLink(
    menuId: string,
    anchorJumpId?: CONTACT_PAGE_ANCHOR_JUMP_POINTS,
    quixMeta?: string
  ): string {
    return this.toEnvironmentUrl(
      `/contact-us?menuId=${menuId}${anchorJumpId ? `#${anchorJumpId}` : ''}${quixMeta ? `&quix=${quixMeta}` : ''}`,
      PROJECTS.PONIES
    );
  }

  contactDmcaHolderLink(quixMeta?: string): string {
    return this.directedContactLink(
      'copyright_dmca_holder',
      CONTACT_PAGE_ANCHOR_JUMP_POINTS.ISSUE_RESPONSE,
      quixMeta
    );
  }

  contactDmcaCounterLink(quixMeta?: string): string {
    return this.directedContactLink(
      'copyright_dmca_counter',
      CONTACT_PAGE_ANCHOR_JUMP_POINTS.ISSUE_RESPONSE,
      quixMeta
    );
  }

  dreamupLink(
    params: {
      prompt?: string;
      persistFromStorage?: boolean;
      stashId?: number;
      /**
       * Will pull the prompt from the associated deviation should it have one
       */
      deviationid?: number;
    } = {}
  ): string {
    return this.toEnvironmentUrl(
      `/dreamup${
        Object.keys(params).length > 0
          ? `?${Object.entries(params)
              .map(([key, value]) => `${key}=${value}`)
              .join('&')}`
          : ''
      }`,
      PROJECTS.STASH
    );
  }

  videoCreatorLink(
    params: {
      deviation?: PapiDeviation;
      prompt?: string;
      deviationid?: number;
    } = {}
  ): string {
    const { deviation, prompt, deviationid } = params;
    let devId = deviationid;
    if (deviation && !deviationid) {
      devId = deviation.isPublished ? deviation.deviationId : deviation.stashPrivateid;
    }
    const queryParamStr = new URLSearchParams(
      omitBy(
        {
          ...(devId != null && { deviationid: String(devId) }),
          prompt: prompt,
        },
        v => v === undefined || v === '' || v === null
      )
    ).toString();
    return this.toEnvironmentUrl(
      `/studio/video${queryParamStr ? `?${queryParamStr}` : ''}`,
      PROJECTS.EDITOR
    );
  }

  muroLink(params: { devid?: number } = {}): string {
    return this.toEnvironmentUrl(
      `/muro/${
        Object.keys(params).length > 0
          ? `?${Object.entries(params)
              .map(([key, value]) => `${key}=${value}`)
              .join('&')}`
          : ''
      }`
    );
  }

  userSubscriptionsLink(params: UserSubscriptionParams): string {
    return this.toEnvironmentUrl(this.userSubscriptionsPath(params), PROJECTS.PROFILE);
  }

  userSubscriptionsPath({
    username,
    tierDeviationId,
    tierContentType,
    anchor,
    landedFromCheckout,
  }: UserSubscriptionParams): string {
    const pathParts = [username.toLowerCase(), 'subscriptions'];
    if (tierDeviationId) {
      pathParts.push(`${tierDeviationId}`);
      if (tierContentType) {
        pathParts.push(tierContentType);
      }
    }
    let path = `/${pathParts.join('/')}`;
    if (landedFromCheckout) {
      path += '?checkout=1';
    }
    return `${path}${anchor ? '#content' : ''}`;
  }

  userSubscriptionsDeeplink({ username, tierEntityId, anchor = false }): string {
    const sectionUrl = new URL(
      this.userLink(username, USER_PROFILE_SECTION_PATHS.SUBSCRIPTIONS, anchor)
    );
    sectionUrl.searchParams.set('tier', tierEntityId);
    return sectionUrl.href;
  }

  userPostsLink(params: UserPostsParams): string {
    return this.toEnvironmentUrl(this.userPostsPath(params), PROJECTS.PROFILE);
  }

  userPostsPath({ username, subPath, sortOrder, anchor }: UserPostsParams) {
    const pathParts = [
      username.toLowerCase(),
      USER_PROFILE_SECTION_PATHS.POSTS,
      subPath,
      sortOrder,
    ].filter(value => value);
    return `/${pathParts.join('/')}${anchor ? '#content' : ''}`;
  }

  giveCoreMembership(username: string) {
    return this.toEnvironmentUrl(
      `${[URLS.CORE_MEMBERSHIP]}/?mx=gift&subscribe=${username.toLowerCase()}`,
      PROJECTS.PONIES
    );
  }

  coreMembershipLink(params: CoreMembershipLinkParams = {}): string {
    let baseUrl: string = URLS.CORE_MEMBERSHIP;

    if (params.forUserId) {
      baseUrl += '/gift';
    } else if (!params.hasCoreMembership) {
      baseUrl += '/buy';
    }

    const url = new URL(baseUrl);

    if (params.referer) {
      url.searchParams.set('referer', params.referer);
    }

    if (params.subproductId) {
      url.searchParams.set('id', params.subproductId.toString());
    }

    return this.toEnvironmentUrl(url.pathname + url.search, PROJECTS.PONIES);
  }

  messagesNotesLink(params: MessagesNotesParams = {}): string {
    const url = new URL(URLS.MESSAGES_NOTES);

    if (params.folderId) {
      url.searchParams.set('folder_id', `${params.folderId}`);
    }
    if (params.noteId) {
      url.searchParams.set('note_id', `${params.noteId}`);
    }
    if (params.q) {
      url.searchParams.set('q', params.q);
    }
    if (params.page) {
      url.searchParams.set('page', `${params.page}`);
    }

    return this.toEnvironmentUrl(url.pathname + url.search, PROJECTS.DAMZ);
  }

  userShopLink(
    username: string,
    subPath: USER_PROFILE_SHOP_SUB_PATHS,
    query?: Record<string, string>,
    anchor?: boolean
  ): string {
    let fullUrl;
    if (Object.keys(query ?? {}).length > 0) {
      const q = new URLSearchParams(query);
      fullUrl = this.toEnvironmentUrl(
        `${this.userShopPath(username, subPath)}?${q.toString()}`,
        PROJECTS.PROFILE
      );
    } else {
      fullUrl = this.toEnvironmentUrl(`${this.userShopPath(username, subPath)}`, PROJECTS.PROFILE);
    }
    return anchor ? `${fullUrl}#content` : fullUrl;
  }

  userShopPath(username: string, subPath: USER_PROFILE_SHOP_SUB_PATHS, anchor?: boolean) {
    return `/${[username.toLowerCase(), USER_PROFILE_SECTION_PATHS.SHOP, subPath]
      .filter(value => value)
      .join('/')}${anchor ? '#content' : ''}`;
  }

  // mirroring the php logic here
  filterDeviationTitle(title: string) {
    let filtered = title.replace(/[^A-Za-z0-9]+/g, '-');
    filtered = trim(filtered, '-');

    if (!filtered) {
      return '-';
    }

    return filtered;
  }

  deviationPath(
    deviationId: number,
    authorName = '', // author can be undefined in PapiDeviation so we are lenient...
    deviationTitle = '',
    base = DEVIATION_PATHS.DEVIATION,
    landingAction: DEVIATION_PAGE_LANDING_ACTION | undefined = undefined
  ) {
    let queryString = '';
    if (landingAction) {
      queryString = `?action=${landingAction}`;
    }

    if (!authorName) {
      // We don't know the author, use the global redirector to find the correct url :)
      return `/deviation/${deviationId}${queryString}`;
    } else {
      authorName = authorName.toLowerCase();
    }

    if (deviationTitle) {
      deviationTitle = `${this.filterDeviationTitle(deviationTitle)}-`;
    }

    return `/${authorName}/${base}/${deviationTitle}${deviationId}${queryString}`;
  }

  deviationLink(
    deviationId: number,
    authorName = '', // author can be undefined in PapiDeviation so we are lenient...
    deviationTitle = '',
    base = DEVIATION_PATHS.DEVIATION,
    landingAction: DEVIATION_PAGE_LANDING_ACTION | undefined = undefined
  ) {
    return this.toEnvironmentUrl(
      this.deviationPath(deviationId, authorName, deviationTitle, base, landingAction),
      PROJECTS.DEVIATION
    );
  }

  /**
   * Runs the PapiDeviation url field through our usual environment conversions and provides a landing action option like
   * the usual {@link deviationLink}.
   *
   * Useful in cases we need to link to either a published deviation or stash deviation and we do not have access to the
   * stashPrivateid field. Usual in user profiles or notifications.
   */
  deviationLinkFromUrl(
    url: string,
    landingAction: DEVIATION_PAGE_LANDING_ACTION | undefined = undefined
  ) {
    return this.toEnvironmentUrl(
      landingAction ? `${url}?action=${landingAction}` : url,
      PROJECTS.DEVIATION
    );
  }

  /**
   * Adoptable source files are hidden behind a redirect. This generates that redirect URL. Backend does some checks to validate access, then redirects the user.
   */
  adoptableSourceFileLink(deviationId: number) {
    return this.buildFromPath(`/deviation/adoptable_src/${deviationId}`);
  }

  moreLikeThisLink(seedDeviationId: number, username: string) {
    const path = `${URLS.MLT}/${username}/${seedDeviationId}`;
    return this.toEnvironmentUrl(path, PROJECTS.BROWSE);
  }

  moreLikeThisCollectionsLink(seedDeviationId: number) {
    return this.toEnvironmentUrl(`${URLS.MLT}/collections/${seedDeviationId}`, PROJECTS.BROWSE);
  }

  moreLikeThisArtistsLink(seedDeviationId: number) {
    return this.toEnvironmentUrl(`${URLS.MLT}/artists/${seedDeviationId}`, PROJECTS.BROWSE);
  }

  /** Links to the browse tag page when given a string tag */
  tagsPageLink(tag: string, params: TagParams = {}) {
    return this.toEnvironmentUrl(`${URLS.TAG}/${tag}${getQueryString(params)}`, PROJECTS.BROWSE);
  }

  /** Links to the daily challenges tag page for today */
  dailyChallengesLink() {
    return this.toEnvironmentUrl('/dailychallenges', PROJECTS.BROWSE);
  }

  forumLink(forumPath: string, threadId?: number) {
    const forumLink = `${URLS.FORUM}/${forumPath}/`;
    return threadId ? `${forumLink}${threadId}` : forumLink;
  }

  printLink(deviationId: number) {
    return `${URLS.ROOT}/print/view/${deviationId}`;
  }

  watchLink() {
    return this.toEnvironmentUrl('/notifications/watch', PROJECTS.MESSAGECENTRE);
  }

  feedbackLink() {
    return this.toEnvironmentUrl(`/notifications`, PROJECTS.MESSAGECENTRE);
  }

  forgotPasswordLink() {
    return this.toEnvironmentUrl(`/users/forgot?kind=password`, PROJECTS.SISU);
  }

  groupNotificationsLink(groupId: number) {
    return this.toEnvironmentUrl(`/notifications?groupId=${groupId}`, PROJECTS.MESSAGECENTRE);
  }

  groupBackroomLink(
    username: string,
    section?: USER_PROFILE_BACKROOM_SECTIONS,
    subPath?: string,
    queryParams?: ProfileQueryParams
  ) {
    return this.toEnvironmentUrl(
      this.groupBackroomPath(username, section, subPath, queryParams),
      PROJECTS.PROFILE
    );
  }

  groupBackroomPath(
    username: string,
    section?: USER_PROFILE_BACKROOM_SECTIONS,
    subPath?: string,
    queryParams?: ProfileQueryParams
  ) {
    return `/${[username.toLowerCase(), USER_PROFILE_SECTION_PATHS.BACKROOM, section, subPath]
      .filter(value => value)
      .join('/')}${getQueryString(queryParams || {})}`;
  }

  groupRosterLink(username: string) {
    return this.toEnvironmentUrl(`/${username}/admin/roster?standalone=1`);
  }

  protectLink(messageId: string) {
    return this.toEnvironmentUrl(
      `/notifications/message/nftprotect/${messageId}`,
      PROJECTS.FEEDBACK
    );
  }

  browseHomeLink(feedType?: 'deviations' | 'posts') {
    return this.toEnvironmentUrl(`/${feedType ?? ''}`, PROJECTS.BROWSE);
  }

  browseDeviantsYouWatchLink(
    feedType: 'deviations' | 'posts' = 'deviations',
    username?: string,
    deviationid?: number
  ) {
    const basePath = `/watch${username ? `/${username.toLowerCase()}` : ''}/${feedType}`;
    const queryString = deviationid ? `?deviationid=${deviationid}` : '';
    return this.toEnvironmentUrl(`${basePath}${queryString}`, PROJECTS.BROWSE);
  }

  browsePopularLink(feedType: 'deviations' | 'posts' = 'deviations') {
    return this.toEnvironmentUrl(`/popular/${feedType}`, PROJECTS.BROWSE);
  }

  browseNewestLink(feedType: 'deviations' | 'posts' = 'deviations') {
    return this.toEnvironmentUrl(`/new/${feedType}`, PROJECTS.BROWSE);
  }

  browseGroupFeedLink(groupName: string, feedType: 'deviations' | 'posts' = 'deviations') {
    return this.toEnvironmentUrl(`/groups/${groupName}/${feedType}`, PROJECTS.BROWSE);
  }

  searchLink(q?: string, basePath = '/search') {
    const path = `${basePath}${q ? getQueryString({ q }) : ''}`;
    return this.toEnvironmentUrl(path, PROJECTS.BROWSE);
  }

  browsePostsLink() {
    return this.toEnvironmentUrl('/posts', PROJECTS.BROWSE);
  }

  browseCommissionsLink() {
    return this.toEnvironmentUrl('/commissions', PROJECTS.BROWSE);
  }

  browseTopicsLink(topic?: string) {
    const topicPath = topic ? `/${topic}` : '';
    return this.toEnvironmentUrl(`/topic${topicPath}`, PROJECTS.BROWSE);
  }

  browseDailyDeviationsLink(rawDate?: Date) {
    let date;
    if (rawDate) {
      const fullYear = rawDate.getFullYear();
      const month = rawDate.getMonth() + 1;
      const day = rawDate.getDate();
      const twoDigitMonth = month > 9 ? month : `0${month}`;
      const dateOfMonth = day > 9 ? day : `0${day}`;
      date = `${fullYear}-${twoDigitMonth}-${dateOfMonth}`;
    }

    const path = `/daily-deviations${getQueryString({ date })}`;
    return this.toEnvironmentUrl(path, PROJECTS.BROWSE);
  }

  browseJournalsLink({
    order,
    faucet2,
    popularWindow,
  }: { faucet2?: string; order?: string; popularWindow?: string } = {}) {
    const pathParts = getPathParts(['journals', faucet2]);
    const path = `/${pathParts}${getQueryString({
      order,
      popularWindow,
    })}`;
    return this.toEnvironmentUrl(path, PROJECTS.BROWSE);
  }

  commentLink(
    commentableTypeId: number,
    commentableItemId: number,
    commentId: number,
    editId: number | undefined = undefined,
    embed: boolean = false,
    openReply: boolean | undefined = undefined
  ) {
    const path: any = [commentableTypeId, commentableItemId, commentId, editId].filter(Boolean);

    if (embed) {
      path.unshift('embed');
    }
    path.unshift('comments');
    return this.toEnvironmentUrl(
      `/${path.join('/')}${openReply ? `?reply_to=${commentId}` : ''}`,
      PROJECTS.COMMENTS
    );
  }

  loginLink() {
    return this.toEnvironmentUrl('/users/login', PROJECTS.SISU);
  }

  joinLink(
    joinpoint?: string,
    returnTo?: string,
    returnType?: string,
    options?: { daDreamup?: boolean }
  ) {
    // The 'joinpoint' query param here is used by BI to
    // track where the signup link came from.
    const query = getQueryString({
      joinpoint,
      referer: returnTo,
      refererType: returnType,
      ...omitBy({ da_dreamup: options?.daDreamup ? 1 : undefined }, value => value === undefined),
    });

    return this.toEnvironmentUrl(
      // the slash at the end is required to avoid a double redirect by haproxy
      `/join/${query}`,
      PROJECTS.SISU
    );
  }

  notesHomeLink(): string {
    return this.toEnvironmentUrl(`/notifications/notes/`);
  }

  newNoteLink(): string {
    return `${this.notesHomeLink()}#to=`;
  }

  groupnoteLink(username: string, noteId?: number): string {
    return this.userLink(username, `notes/${noteId}`);
  }

  groupLogLink(username: string, messageId: string): string {
    return this.userLink(username, `messages/${messageId}/`);
  }

  badgesLink(username: string, badgeId?: number): string {
    return this.userLink(username, 'badges') + (badgeId ? `/${badgeId}/` : '');
  }

  userReactivatedModalLink(username: string) {
    return `${this.userLink(username)}?reactivated=1`;
  }

  userNoteLink(username: string): string {
    return this.toEnvironmentUrl(`${URLS.NOTES}/#to=${username.toLowerCase()}`);
  }

  userMessageLink(username: string): string {
    return this.toEnvironmentUrl(`${URLS.MESSAGES}?to=${username.toLowerCase()}`);
  }

  chatroomInStandalone(chatroomId: string, tab?: string): string {
    const tabQuery = tab ? `&tab=${tab}` : '';
    return this.toEnvironmentUrl(`${URLS.CHAT_HOME}?room=${chatroomId}${tabQuery}`, PROJECTS.CHAT);
  }

  directMessageInStandalone(userUuid: string): string {
    return this.toEnvironmentUrl(`${URLS.CHAT_HOME}?dm=${userUuid}`, PROJECTS.CHAT);
  }

  staticCDNLink(resource: string): string {
    return `${URLS.CDN}/${resource}`;
  }

  checkoutLink(subProductId: number, foruserid?: number): string {
    return this.toEnvironmentUrl(
      `/purchase${getQueryString({
        id: subProductId,
        foruserid,
      })}`,
      PROJECTS.PONIES
    );
  }

  checkoutCompleteLink(orderGroupId: number, referer?: string): string {
    return this.toEnvironmentUrl(
      `/purchase/complete${getQueryString({
        id: orderGroupId,
        referer,
      })}`,
      PROJECTS.PONIES
    );
  }

  earningsDownloadLink(month?: number, year?: number): string {
    return (
      this.toEnvironmentUrl(URLS.EARNINGS_DOWNLOAD, PROJECTS.PONIES) +
      (month && year ? getQueryString({ month, year }) : '')
    );
  }

  account(tierId?: number): string {
    return this.toEnvironmentUrl(`/account${tierId ? `?tier=${tierId}` : ''}`, PROJECTS.PONIES);
  }

  customerDashboard(subproductId?: number): string {
    return this.toEnvironmentUrl(
      `/account/customers${subproductId ? `?subproductid=${subproductId}` : ''}`,
      PROJECTS.PONIES
    );
  }

  /**
   * Provided a deviation's id, this will return a link to our submit page with the submission prepoulated with said deviation.
   *
   * This is useful for products built on top of stash.
   */
  submissionLinkWithId(deviationId: number) {
    return this.toEnvironmentUrl(`/_deviation_submit?deviationid=${deviationId}`);
  }

  submissionLinkWithGroupId(groupId: number) {
    return this.studio({ openSubmitForm: true });
  }

  studio(
    options: {
      section?:
        | 'drafts'
        | 'literature'
        | 'journals'
        | 'scheduled'
        | 'published'
        | 'discounts'
        | 'shop-reports'
        | 'home'
        | 'stash'
        | 'apps'
        | 'apps-unpublished'
        | 'earnings'
        | 'orders-status'
        | 'orders-originals';
      playground?: 'root' | 'dreamup' | 'drawings' | 'inspiration' | 'wixel' | 'draw' | 'video';
      discounts?: 'active' | 'scheduled' | 'ended' | 'active_and_scheduled' | 'all';
      published?: 'root' | 'galleries' | 'tiers';
      products?:
        | 'root'
        | 'tier'
        | 'tier-unlisted'
        | 'commission'
        | 'exclusive'
        | 'exclusive_resale'
        | 'exclusive-unlisted'
        | 'originals'
        | 'originals-unlisted'
        | 'premium_gallery'
        | 'pcp'
        | 'pcp-unlisted'
        | 'originals-drafts';
      searchQuery?: string;
      openSubmitForm?: boolean;
      openCreateForm?: boolean;
      tierId?: number;
      folderId?: string;
      deviationId?: number;
      contentType?: 'deviations' | 'posts' | 'subfolders';
    } = {}
  ) {
    const params = new URLSearchParams();
    const {
      section = 'drafts',
      playground,
      discounts,
      published,
      products,
      searchQuery,
      openSubmitForm,
      openCreateForm,
      tierId,
      contentType,
      deviationId,
    } = options;
    let path;

    if (
      ['root', 'dreamup', 'drawings', 'inspiration', 'wixel', 'draw', 'video'].includes(
        playground ?? ''
      )
    ) {
      switch (playground) {
        case 'dreamup':
          path = '/studio/dreamup';
          break;
        case 'drawings':
          path = '/studio/drawings';
          break;
        case 'draw':
          path = '/studio/draw';
          break;
        case 'video':
          path = '/studio/video';
          break;
        case 'inspiration':
          path = '/studio/inspiration';
          break;
        case 'wixel':
          path = '/studio/wixel';
          break;
        case 'root':
        default:
          path = '/studio/playground';
          break;
      }

      if (deviationId) {
        params.append('deviationid', deviationId.toString());
      }
    } else if (
      section === 'published' ||
      ['root', 'galleries', 'tiers'].includes(published ?? '')
    ) {
      switch (published) {
        case 'galleries':
          path = '/studio/published/galleries';
          break;
        case 'tiers':
          path = '/studio/published/tiers';
          break;
        case 'root':
        default:
          path = '/studio/published';
          if (options.folderId) {
            path = `${path}/${options.folderId}`;
          }
          if (options.contentType) {
            path = `${path}/${contentType}`;
          }
      }
    } else if (products) {
      switch (products) {
        case 'tier':
          path = '/studio/subscriptions';
          break;
        case 'tier-unlisted':
          path = '/studio/subscriptions-drafts';
          break;
        case 'commission':
          path = '/studio/commissions';
          break;
        case 'originals':
          path = '/studio/originals';
          break;
        case 'originals-drafts':
          path = '/studio/originals-drafts';
          break;
        case 'exclusive':
          path = '/studio/exclusives';
          break;
        case 'exclusive-unlisted':
          path = '/studio/unlisted-exclusives';
          break;
        case 'premium_gallery':
          path = '/studio/premium-galleries';
          break;
        case 'pcp':
          path = '/studio/pcp';
          break;
        case 'pcp-unlisted':
          path = '/studio/unlisted-pcp';
          break;
        case 'originals-unlisted':
          path = '/studio/originals-drafts';
          break;
        case 'root':
        default:
          path = '/studio/shop-products';
          break;
      }
    } else if (section === 'drafts') {
      path = '/studio';
    } else if (section === 'literature') {
      path = '/studio/literature';
    } else if (section === 'journals') {
      path = '/studio/journals';
    } else if (section === 'scheduled') {
      path = '/studio/scheduled';
    } else if (section === 'shop-reports') {
      path = '/studio/shop-reports';
    } else if (section === 'home') {
      path = '/studio/home';
    } else if (section === 'stash') {
      path = '/stash';
      if (options.folderId) {
        path = `/stash/${options.folderId}`;
      }
    } else if (section === 'apps') {
      path = '/studio/apps';
    } else if (section === 'apps-unpublished') {
      path = '/studio/apps-unpublished';
    } else if (section === 'earnings') {
      path = '/studio/earnings';
    } else if (section === 'orders-status') {
      path = '/studio/orders';
    } else if (section === 'orders-originals') {
      path = '/studio/orders/originals';
      if (deviationId) {
        params.append('deviationid', deviationId.toString());
      }
    } else if (
      ['active', 'scheduled', 'ended', 'active_and_scheduled', 'all'].includes(discounts ?? '')
    ) {
      switch (discounts) {
        case 'active':
          path = '/studio/discounts/active';
          break;
        case 'scheduled':
          path = '/studio/discounts/scheduled';
          break;
        case 'ended':
          path = '/studio/discounts/ended';
          break;
        case 'active_and_scheduled':
          path = '/studio/discounts/active_and_scheduled';
          break;
        case 'all':
          path = '/studio/discounts/all';
          break;
        default:
          break;
      }
    }

    // all stash sections support search
    if (searchQuery) {
      params.append('q', searchQuery);
    }

    if (openSubmitForm) {
      params.append('new', '1');
      if (tierId) {
        params.append('fortier', `${tierId}`);
      }
    } else if (openCreateForm) {
      params.append('create', '1');
    }

    const querystring = params.size > 0 ? `?${params}` : '';
    return this.toEnvironmentUrl(`${path}${querystring}`, PROJECTS.EDITOR);
  }

  studioStash(folderId?: number) {
    const encodedFolderId = folderId && `2${folderId.toString(36)}`;
    return this.toEnvironmentUrl(
      folderId ? `/stash/${encodedFolderId}` : '/stash',
      PROJECTS.EDITOR
    );
  }

  studioGallery(folderId?: number) {
    return this.toEnvironmentUrl(
      folderId
        ? folderId === SCRAPS_FOLDER_ID
          ? '/studio/published/scraps'
          : `/studio/published/${folderId}`
        : '/studio/published/galleries',
      PROJECTS.EDITOR
    );
  }

  studioTier(tierId: number, contentType: 'deviations' | 'posts' = 'deviations') {
    return this.toEnvironmentUrl(`/studio/tier/${tierId}/${contentType}`, PROJECTS.EDITOR);
  }

  studioFolder(folderId?: number, folderType?: string) {
    switch (folderType) {
      case 'stash':
        return this.studioStash(folderId);
      case 'gallery':
        return this.studioGallery(folderId);
    }
  }

  stashFolderZipDownload(folderId: number) {
    const encodedFolderId = `2${folderId.toString(36)}`;
    return `${URLS.STASH}zip/${encodedFolderId}`;
  }

  shopLink(path?: SHOP_PATHS) {
    if (!path) {
      return URLS.SHOP;
    }
    return `${URLS.SHOP}/${path}`;
  }

  /**
   * Link to a stash item
   */
  stashItemLink(
    base36Id: string,
    landingAction: DEVIATION_PAGE_LANDING_ACTION | undefined = undefined
  ) {
    const id = base36Id.startsWith('0') ? base36Id : `0${base36Id}`;
    const stashUrl = this.toEnvironmentUrl(`/stash/${id}`, PROJECTS.DEVIATION);
    return landingAction ? `${stashUrl}?action=${landingAction}` : stashUrl;
  }

  /**
   * Link to stash folder
   */
  stashFolderLink(folderId: number) {
    return this.to(`${URLS.STASH}2${folderId.toString(36)}`);
  }

  settingsLink(
    endpoint:
      | 'deactivation'
      | 'general'
      | 'browsing'
      | 'auth'
      | 'notifications'
      | 'promos'
      | 'personaldata',
    widgetId?: string,
    params?: Record<string, string>
  ) {
    return this.toEnvironmentUrl(
      `/account/${endpoint}${widgetId ? `#${widgetId}` : ''}${params ? `?${new URLSearchParams(params).toString()}` : ''}`,
      PROJECTS.MISC
    );
  }

  /**
   * Parse URL and return components:
   * - `path` - everything up to '?' symbol
   * - `query` - query part after the '?' symbol, excluding fragment if any
   * - `fragment` - fragment part after the '#' symbol
   *
   * @param url Absolute or relative URL
   * @returns object
   */
  parseUrl(url: string) {
    const [pathWithQuery, fragment] = this._split(url, '#');
    const [path, query] = this._split(pathWithQuery, '?');
    return {
      path,
      fragment,
      query,
    };
  }

  private toEnvironmentUrl(path: string, projectFlag?: PROJECTS): string {
    if (path.startsWith(URLS.ROOT)) {
      path = path.replace(/^https:\/\/www.deviantart.com/, '');
    }
    this.checkServerInit();
    if (
      (typeof window !== 'undefined' && window['__XHR_LOCAL__']) ||
      (typeof process !== 'undefined' && process.env['XHR_ENV'] === 'vm')
    ) {
      // Client-side URLs to any project on the VM
      return `${this.origin()}${path}`;
    }
    if (projectFlag && this.hasEnvironmentFlag(projectFlag)) {
      // If the url belongs to the app we're running, point to it
      return `${this.baseUrl(false)}${path}`;
    } else if (!this.isProduction) {
      // Otherwise, if we're on vm, point to deviantart.lan domain
      return this.buildFromPath(path);
    }
    // Else, build path using GA as base path
    return `${URLS.ROOT}${path}`;
  }

  private hasEnvironmentFlag(flag: PROJECTS | string): boolean {
    if (this.isProduction) {
      return false;
    }
    return Boolean((this.environmentFlags || []).find(v => v === flag));
  }

  private checkServerInit() {
    if ((typeof document === 'undefined' || typeof window === 'undefined') && !this.initSSRDone) {
      ddt.log('URL', 'SSR url was not initialised before use, you probably have two instances');
    }
  }

  kbLink(url: string, language: string): string {
    return url.replace('/en/', `/${language}/`);
  }

  private _split(s: string, separator: string) {
    const p = s.indexOf(separator);
    if (p >= 0) {
      return [s.substring(0, p), s.substring(p + separator.length)];
    }
    return [s, ''];
  }
}
